import { useState, useCallback, useEffect, useRef } from 'react';
import { useApp } from '../context/AppContext';
import { parseRoster, hasClaudeApiKey } from '../services/claudeApi';
import { captureRosterPhoto } from '../services/cameraService';
import { findCandidatesForLegs, SCHEDULE_LOOKUP_ENABLED } from '../services/scheduleService';
import type { ParsedRosterEntry, ImageInput, Flight, CandidateFlight } from '../types';

export type ImportStep = 'input' | 'animating' | 'matching' | 'results';

export interface SaveResult {
  insertedCount: number;
  duplicatesSkipped: number;
}

const SESSION_KEY = 'rostfound-roster-import';

interface PersistedImportState {
  step: ImportStep;
  rosterText: string;
  rosterMonth: number;
  rosterYear: number;
  parsedEntries: ParsedRosterEntry[];
  selectedIndices: number[];
  matchCandidates: [number, CandidateFlight[]][];
  matchSelections: [number, string | null][];
}

function saveImportState(state: PersistedImportState) {
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify(state));
  } catch { /* quota exceeded or blocked — ignore */ }
}

function loadImportState(): PersistedImportState | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as PersistedImportState;
  } catch {
    return null;
  }
}

function clearImportState() {
  try { localStorage.removeItem(SESSION_KEY); } catch { /* ignore */ }
}

function isUnmatchedFlight(e: ParsedRosterEntry): boolean {
  return e.type === 'flight' && !e.flightNumber && !!e.departureAirport && !!e.arrivalAirport;
}

export function useRosterImport(calendarMonth?: number, calendarYear?: number, skipMatching = false) {
  const { currentUser, addFlights } = useApp();

  // Restore persisted state on first mount (lazy ref pattern)
  const restoredRef = useRef<PersistedImportState | null | 'cleared'>(undefined as any);
  if (restoredRef.current === undefined) {
    restoredRef.current = loadImportState();
  }
  const restored = restoredRef.current === 'cleared' ? null : restoredRef.current;

  // Month selector — defaults to restored > calendar view month > current month
  const now = new Date();
  const [rosterMonth, setRosterMonth] = useState(restored?.rosterMonth ?? calendarMonth ?? (now.getMonth() + 1));
  const [rosterYear, setRosterYear] = useState(restored?.rosterYear ?? calendarYear ?? now.getFullYear());

  // Sync month/year when calendar view changes (e.g. modal reopens on different month)
  // Skip on first mount if we restored from localStorage (don't overwrite restored month)
  const skipFirstSync = useRef(!!restored);
  useEffect(() => {
    if (skipFirstSync.current) {
      skipFirstSync.current = false;
      return;
    }
    if (calendarMonth != null) setRosterMonth(calendarMonth);
    if (calendarYear != null) setRosterYear(calendarYear);
  }, [calendarMonth, calendarYear]);

  // 'animating' is transient — restore to 'results' if interrupted mid-animation
  const restoredStep = restored?.step === 'animating' ? 'results' : restored?.step;
  const [step, setStep] = useState<ImportStep>(restoredStep ?? 'input');
  const [rosterText, setRosterText] = useState(restored?.rosterText ?? '');
  const [images, setImages] = useState<ImageInput[]>([]);
  const [parsing, setParsing] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const [parsedEntries, setParsedEntries] = useState<ParsedRosterEntry[]>(restored?.parsedEntries ?? []);
  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(
    new Set(restored?.selectedIndices ?? [])
  );
  const [saving, setSaving] = useState(false);
  const [saveResult, setSaveResult] = useState<SaveResult | null>(null);

  // Flight matching state
  const [matchCandidates, setMatchCandidates] = useState<Map<number, CandidateFlight[]>>(
    new Map(restored?.matchCandidates ?? [])
  );
  const [matchSelections, setMatchSelections] = useState<Map<number, string | null>>(
    new Map(restored?.matchSelections ?? [])
  );
  const [loadingCandidates, setLoadingCandidates] = useState(false);

  // Track what step comes after the globe animation
  const nextStepAfterAnimation = useRef<'matching' | 'results'>('results');

  // Clear restored ref after first render so it doesn't interfere
  useEffect(() => {
    restoredRef.current = null;
  }, []);

  // Persist import state to sessionStorage on meaningful changes
  useEffect(() => {
    // Only persist if there's something worth saving (beyond empty input step)
    if (step === 'input' && !rosterText.trim() && parsedEntries.length === 0) {
      clearImportState();
      return;
    }
    saveImportState({
      step,
      rosterText,
      rosterMonth,
      rosterYear,
      parsedEntries,
      selectedIndices: [...selectedIndices],
      matchCandidates: [...matchCandidates],
      matchSelections: [...matchSelections],
    });
  }, [step, rosterText, rosterMonth, rosterYear, parsedEntries, selectedIndices, matchCandidates, matchSelections]);

  const canUseLLM = hasClaudeApiKey();

  const submitRoster = useCallback(async () => {
    if (!rosterText.trim() && images.length === 0) {
      setParseError('Paste roster text or upload an image');
      return;
    }

    // Show the globe immediately while AI parses in background
    setStep('animating');
    setParsing(true);
    setParseError(null);

    const monthHint = `${rosterYear}-${String(rosterMonth).padStart(2, '0')}`;
    const result = await parseRoster(rosterText, images.length > 0 ? images : undefined, currentUser.baseAirport, currentUser.airlineCode, monthHint);

    if (result.error || result.entries.length === 0) {
      setParseError(result.error || 'No entries found in roster');
      setStep('input');
      setParsing(false);
      return;
    }

    setParsedEntries(result.entries);

    // Check for unmatched flights (have route but no flight number)
    const unmatchedLegs = result.entries
      .map((e, i) => ({ entry: e, index: i }))
      .filter(({ entry }) => isUnmatchedFlight(entry));

    // Auto-select all flights for the results step
    const flightIndices = new Set<number>();
    result.entries.forEach((e, i) => {
      if (e.type === 'flight') flightIndices.add(i);
    });
    setSelectedIndices(flightIndices);

    if (SCHEDULE_LOOKUP_ENABLED && unmatchedLegs.length > 0 && !skipMatching) {
      nextStepAfterAnimation.current = 'matching';

      // Kick off candidate lookup in parallel (runs during globe animation)
      setLoadingCandidates(true);
      findCandidatesForLegs(
        unmatchedLegs.map(({ entry, index }) => ({
          index,
          origin: entry.departureAirport!,
          destination: entry.arrivalAirport!,
          date: entry.date,
          airlineCode: entry.airlineCode,
        }))
      ).then((candidates) => {
        setMatchCandidates(candidates);
        const selections = new Map<number, string | null>();
        for (const [idx, cands] of candidates) {
          selections.set(idx, cands.length === 1 ? cands[0].id : null);
        }
        setMatchSelections(selections);
        setLoadingCandidates(false);
      });
    } else {
      nextStepAfterAnimation.current = 'results';
    }

    // Stop loading spinner — this triggers the globe animation to start
    setParsing(false);
  }, [rosterText, images]);

  const selectCandidate = useCallback((entryIndex: number, candidateId: string | null) => {
    setMatchSelections((prev) => {
      const next = new Map(prev);
      next.set(entryIndex, candidateId);
      return next;
    });
  }, []);

  const completeAnimation = useCallback(() => {
    setStep(nextStepAfterAnimation.current);
  }, []);

  const confirmMatches = useCallback(() => {
    // Merge selected candidate data into parsed entries
    setParsedEntries((prev) =>
      prev.map((entry, i) => {
        const selectedId = matchSelections.get(i);
        if (!selectedId) return entry;

        const candidates = matchCandidates.get(i);
        const candidate = candidates?.find((c) => c.id === selectedId);
        if (!candidate) return entry;

        return {
          ...entry,
          flightNumber: candidate.flightNumber,
          departureTime: candidate.departureTime,
          arrivalTime: candidate.arrivalTime,
          aircraftType: candidate.aircraftType,
        };
      })
    );

    // Auto-select all flights for the results step
    const flightIndices = new Set<number>();
    parsedEntries.forEach((e, i) => {
      if (e.type === 'flight') flightIndices.add(i);
    });
    setSelectedIndices(flightIndices);
    setStep('results');
  }, [matchSelections, matchCandidates, parsedEntries]);

  const toggleEntry = useCallback((index: number) => {
    setSelectedIndices((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  }, []);

  const selectAllFlights = useCallback(() => {
    const flightIndices = new Set<number>();
    parsedEntries.forEach((e, i) => {
      if (e.type === 'flight') flightIndices.add(i);
    });
    setSelectedIndices(flightIndices);
  }, [parsedEntries]);

  const deselectAll = useCallback(() => {
    setSelectedIndices(new Set());
  }, []);

  const updateEntry = useCallback((index: number, updates: Partial<ParsedRosterEntry>) => {
    setParsedEntries((prev) => prev.map((e, i) => (i === index ? { ...e, ...updates } : e)));
  }, []);

  const removeEntry = useCallback((index: number) => {
    setParsedEntries((prev) => prev.filter((_, i) => i !== index));
    setSelectedIndices((prev) => {
      const next = new Set<number>();
      for (const idx of prev) {
        if (idx < index) next.add(idx);
        else if (idx > index) next.add(idx - 1);
      }
      return next;
    });
  }, []);

  const saveSelected = useCallback(async (): Promise<SaveResult> => {
    setSaving(true);
    setSaveResult(null);

    const selectedFlights = parsedEntries.filter(
      (e, i) =>
        selectedIndices.has(i) &&
        e.type === 'flight' &&
        (e.flightNumber || (e.departureAirport && e.arrivalAirport))
    );

    const flights: Flight[] = selectedFlights.map((entry, i) => ({
      id: `flight-${Date.now()}-${i}`,
      userId: currentUser.id,
      flightNumber: entry.flightNumber || '',
      date: entry.date,
      departureAirport: entry.departureAirport || '',
      arrivalAirport: entry.arrivalAirport || '',
      departureTime: entry.departureTime,
      arrivalTime: entry.arrivalTime,
      airlineCode: entry.airlineCode || currentUser.airlineCode,
      ...(entry.aircraftType ? { aircraftType: entry.aircraftType } : {}),
      source: 'llm_import',
    }));

    let result: SaveResult = { insertedCount: 0, duplicatesSkipped: 0 };

    if (flights.length > 0) {
      const addResult = await addFlights(flights);
      result = {
        insertedCount: addResult.inserted.length,
        duplicatesSkipped: addResult.duplicatesSkipped,
      };
    }

    setSaveResult(result);
    if (result.insertedCount > 0) {
      clearImportState();
      const { track } = await import('../analytics');
      track('roster_imported', { inserted: result.insertedCount, duplicates: result.duplicatesSkipped });
    }
    setSaving(false);
    return result;
  }, [parsedEntries, selectedIndices, currentUser, addFlights]);

  const addImage = useCallback(async (file: File) => {
    const mediaType = file.type as ImageInput['mediaType'];
    if (!['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(mediaType)) {
      return;
    }

    const base64Data = await fileToBase64(file);
    setImages((prev) => [...prev, { mediaType, base64Data }]);
  }, []);

  const removeImage = useCallback((index: number) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const addSharedImage = useCallback((image: { base64Data: string; mediaType: string }) => {
    setImages([{ mediaType: image.mediaType as ImageInput['mediaType'], base64Data: image.base64Data }]);
  }, []);

  const addCameraImage = useCallback(async () => {
    const result = await captureRosterPhoto();
    if (result) {
      setImages((prev) => [...prev, { mediaType: result.mediaType, base64Data: result.base64Data }]);
    }
  }, []);

  const reset = useCallback(() => {
    clearImportState();
    setStep('input');
    setRosterText('');
    setImages([]);
    setParsing(false);
    setParseError(null);
    setParsedEntries([]);
    setSelectedIndices(new Set());
    setSaving(false);
    setSaveResult(null);
    setMatchCandidates(new Map());
    setMatchSelections(new Map());
    setLoadingCandidates(false);
  }, []);

  const selectedFlightCount = parsedEntries.filter(
    (e, i) => selectedIndices.has(i) && e.type === 'flight'
  ).length;

  return {
    step,
    rosterMonth,
    rosterYear,
    setRosterMonth,
    setRosterYear,
    rosterText,
    setRosterText,
    images,
    parsing,
    parseError,
    parsedEntries,
    selectedIndices,
    selectedFlightCount,
    saving,
    saveResult,
    canUseLLM,
    submitRoster,
    toggleEntry,
    selectAllFlights,
    deselectAll,
    saveSelected,
    updateEntry,
    removeEntry,
    addImage,
    addSharedImage,
    addCameraImage,
    removeImage,
    reset,
    // Globe animation
    completeAnimation,
    // Flight matching
    matchCandidates,
    matchSelections,
    loadingCandidates,
    selectCandidate,
    confirmMatches,
  };
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.split(',')[1]);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
